/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import {
    type AfterViewInit,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    type SimpleChanges,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { LocalStorageService, PermissionService } from '@dm/core';
import { ActionType, infigo } from '@dm/live-material-infigo';
import { FeatureConfig } from 'app/config/feature';
import { concat as _concat, debounce as _debounce } from 'lodash';
import { type Observable, type Subscription, merge, of, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { AuthService } from '../../../../common/services';
import { GlobalSearchService } from '../global-search.service';
import { type SearchData, DEBOUNCE_TIME, GlobalSearchStrService, GroupTypeE, INCREMENT } from '../global-search-str.service';

const isFirstKey = 'infigo_first_';

@Component({
    selector: 'app-global-search-input',
    templateUrl: './global-search-input.component.html',
    styleUrls: ['./global-search-input.component.scss', '../global-search.component.scss'],
})
export class GlobalSearchInputComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() baseCount = INCREMENT;
    @Input() onSwitchSize: () => void;
    @Input() initValue;
    @Output() searchResultChange = new EventEmitter<{ [key: string]: number | { [key: string]: SearchData[] } | string | boolean }>();

    @ViewChild('input') input: ElementRef;
    showAnimate = false;
    i18n = this.strService.i18n;
    timer;
    value = '';

    sourceData: { [key: string]: SearchData[] } = {
        objects: [],
        operations: [],
        pages: [],
        helps: [],
    };

    container: HTMLElement;

    debouncedSearch: any;

    searchResultSub$: Subscription;

    private destroy$ = new Subject<void>();

    constructor(
        private strService: GlobalSearchStrService,
        private service: GlobalSearchService,
        private cdr: ChangeDetectorRef,
        public permissionService: PermissionService,
        private localStorageService: LocalStorageService,
        private authService: AuthService,
    ) {
    }

    /**
     * NG_VALUE_ACCESSOR接口
     */
    onChangeInterface: (_: string) => void = () => null;

    onTouchedInterface: () => void = () => null;

    ngOnChanges(changes: SimpleChanges): void {
        const { baseCount } = changes;

        if (baseCount) {
            this.search(this.value, this.baseCount > INCREMENT);
        }
    }

    ngOnInit(): void {
        const isFirstEnter = this.localStorageService.get(isFirstKey + this.authService.getUserName());
        if (isFirstEnter === '' || isFirstEnter !== true) {
            this.showAnimate = true;
        }
        if (this.initValue) {
            this.value = this.initValue;
            this.search(this.value, false);
        } else {
            this.getDefaultData();
        }
        this.clearSelection();
    }

    ngAfterViewInit(): void {
        this.container = document.querySelector('.global-search-list-container');
        this.debouncedSearch = _debounce(this.search, DEBOUNCE_TIME);
        // 确保拿到input
        setTimeout(() => {
            this.input.nativeElement.focus();
        });
    }

    expand(): void {
        const storageKey = isFirstKey + this.authService.getUserName();
        const isFirstEnter = this.localStorageService.get(storageKey);
        if (isFirstEnter === '' || isFirstEnter !== true) {
            this.localStorageService.set(storageKey, true);
        }

        if (this.timer) {
            clearTimeout(this.timer);
        }
        if (this.onSwitchSize) {
            infigo.store.dispatch({ type: ActionType.UserInput, value: this.value });
            this.onSwitchSize();
        }
    }

    ngOnDestroy(): void {
        this.clearSelection();
        this.clear();
        this.value = '';
        this.baseCount = INCREMENT;
        this.destroy$.next();
        this.destroy$.complete();
        clearTimeout(this.timer);
    }

    /**
     * 输入框值改变
     * @param value
     */
    onInputChange(value: string): void {
        this.value = value;
        this.baseCount = INCREMENT;
        this.debouncedSearch(this.value, false);
    }

    /**
     * 查询数据
     * @param value
     * @param more
     */
    search(value: string, more = false): void {
        this.clearSearchResult(more);

        // 保证当前流只有一个
        if (this.searchResultSub$) {
            this.searchResultSub$.unsubscribe();
        }

        const searchResult$ = value ? merge(this.getObject(value, more), this.getPageAndOperation(value)) : this.getPageAndOperation(value);

        this.searchResultSub$ = searchResult$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (!more) {
                this.clearSelection();
            }
            this.searchResultChange.emit({
                sourceData: this.sourceData,
                baseCount: this.baseCount,
                value,
                more,
            });
        });
    }

    /**
     * 查对象
     * @param value
     * @param more
     */
    getObject(value: string, more = false): Observable<SearchData[]> {
        const range = more ? `[${this.baseCount + 1 - INCREMENT}-${this.baseCount + 1}]` : `[0-${this.baseCount + 1}]`;
        return this.service.findObject(value, range).pipe(
            tap((obj: SearchData[]) => {
                if (more) {
                    this.sourceData.objects.push(...obj);
                } else {
                    this.sourceData.objects = obj;
                }
            }),
        );
    }

    /**
     * 判断：
     * 1.是否为超级管理员 1 或者 管理员 2
     * 2.二级存储不可用infigo
     */
    hasExpandPermission(): boolean {
        const roleId = this.authService.getRoleId();
        const isManager = roleId === '1' || roleId === '2';

        return isManager && FeatureConfig.IsSupportAdvanceInfigo;
    }

    /**
     * 查页面和操作
     * @param value
     */
    getPageAndOperation(value: string): Observable<SearchData[]> {
        // 搜索值不为空时，返回全部操作和页面，帮助过滤的结果
        if (value) {
            return this.service.findPageAndOperationHelp(value).pipe(
                tap((obj: SearchData[]) => {
                    obj.map((data) => {
                        if (data.groupType === GroupTypeE.operation) {
                            this.sourceData.operations.push(data);
                        } else if (data.groupType === GroupTypeE.page) {
                            this.sourceData.pages.push(data);
                        } else if (data.groupType === GroupTypeE.help) {
                            this.sourceData.helps.push(data);
                        }
                    });
                }),
            );
        } else {
            // 搜索值为空时，返回默认操作和页面前5条
            this.getDefaultData();
            return of([] as SearchData[]);
        }
    }

    /**
     * 获取默认数据
     */
    getDefaultData(): void {
        this.service.getDefaultShowData();
        this.sourceData.objects = [];
        this.sourceData.operations = this.service.defaultSearchData.operations;
        this.sourceData.pages = this.service.defaultSearchData.pages;
        this.sourceData.helps = [];
        this.searchResultChange.emit({
            sourceData: this.sourceData,
            baseCount: this.baseCount,
        });
    }

    /**
     * 清空源数据
     */
    clearSearchResult(more = false): void {
        if (!more) {
            this.sourceData.objects = [];
        }
        this.sourceData.operations = [];
        this.sourceData.pages = [];
        this.sourceData.helps = [];
    }

    /**
     * 清空输入框
     */
    clear(): void {
        this.value = '';
        this.input.nativeElement.focus();
        this.baseCount = INCREMENT;
        this.onChangeInterface(this.value);
        this.onTouchedInterface();
        this.getDefaultData();
    }

    /**
     * 清空选项
     */
    clearSelection(): void {
        _concat(this.sourceData.objects, this.sourceData.operations, this.sourceData.pages).forEach((item: SearchData) => {
            if (item) {
                item.selected = false;
            }
        });
    }

    writeValue(value: string): void {
        this.value = value || '';
        this.cdr.markForCheck();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChangeInterface = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouchedInterface = fn;
    }
}
